import { Box, Button, ClickAwayListener, Stack, SvgIcon, Typography } from '@mui/material'
import isEqual from 'lodash/isEqual'
import { useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useHover } from '@omnea-app/fe-browser-utils/useHover'
import { ProfileBox } from '@omnea-app/fe-users-and-groups/ProfileBox'
import { UserAutocomplete } from '@omnea-app/fe-users-and-groups/UserAutocomplete'
import { EditOMIcon, PlusOMIcon } from '@omnea-app/icons'
import { UserItem } from '@omnea-app/packages/universal/users-and-groups/userItem'
import { useUpdateRequestProperties } from '../../helpers/useUpdateRequestProperties'

type RequestOwnerVariant = 'REQUEST_TABLE_COLUMN' | 'REQUEST_DETAILS_PAGE'
interface RequestOwnerProps {
  requestId: string
  owner: UserItem | null
  variant: RequestOwnerVariant
}

const AddProjectOwnerActionItem = ({
  editProjectOwner,
  variant,
}: {
  editProjectOwner: () => void
  variant: RequestOwnerVariant
}) => {
  const { t } = useTranslation('requests')
  const isRequestDetailsVariant = isEqual(variant, 'REQUEST_DETAILS_PAGE')

  const handleClick = (event: MouseEvent) => {
    event.preventDefault()
    editProjectOwner()
  }

  if (isRequestDetailsVariant) {
    return (
      <Button
        color="secondary"
        size="small"
        variant="text"
        startIcon={<PlusOMIcon />}
        onClick={handleClick}
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('request-owners.add')}
      </Button>
    )
  }

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      onClick={handleClick}
      sx={{
        width: '100%',
        minHeight: '100%',
        '&:hover': {
          cursor: 'pointer',
          '& .addIcon': {
            display: 'flex',
          },
        },
      }}
    >
      <Stack className="addIcon" sx={{ display: 'none', alignItems: 'center', justifyContent: 'center' }}>
        <SvgIcon component={PlusOMIcon} inheritViewBox sx={{ ml: 1, fontSize: '16px', color: 'fg.brand.default' }} />
      </Stack>
    </Stack>
  )
}

export const RequestOwner = ({ owner, requestId, variant }: RequestOwnerProps) => {
  const { t } = useTranslation('requests')
  const { user } = useAuthenticatedUser()
  const { mutate: updateRequestProperties } = useUpdateRequestProperties()
  const [open, setOpen] = useState(false)
  const [hoverRef, isHovered] = useHover()

  const updateOwner = async (updatedOwner: UserItem | null) => {
    updateRequestProperties({ requestId, ownerId: updatedOwner?.id ?? null })
    setOpen(false)
  }
  const isViewerOwner = user.isViewOnly && owner?.id === user.id
  const canEditOwner = user.isNonViewerAdmin || isViewerOwner
  const editProjectOwner = () => {
    if (!canEditOwner) return
    setOpen(true)
  }
  const handleClickAway = () => setOpen(false)
  const name = owner?.name

  if (open) {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Stack onClick={(event) => event.preventDefault()} sx={{ maxHeight: 40, minWidth: 200 }}>
          <UserAutocomplete autoFocus onChange={updateOwner} value={owner} />
        </Stack>
      </ClickAwayListener>
    )
  }
  if (name) {
    return (
      <Box
        ref={hoverRef}
        sx={{
          display: 'flex',
          overflow: 'hidden',
          justifyContent: 'space-between',
          alignItems: 'center',
          ...(canEditOwner && {
            '&:hover': {
              cursor: 'pointer',
            },
          }),
        }}
        onClick={(event) => {
          event.preventDefault()
          editProjectOwner()
        }}
      >
        <ProfileBox sx={{ pt: 1 }} avatarVariant="circular" text={owner?.name} userId={owner?.id || null} />
        {isHovered && canEditOwner && (
          <SvgIcon component={EditOMIcon} inheritViewBox sx={{ ml: 2, fontSize: '16px', color: 'fg.subtle' }} />
        )}
      </Box>
    )
  }
  if (user.isNonViewerAdmin) {
    return <AddProjectOwnerActionItem editProjectOwner={editProjectOwner} variant={variant} />
  }
  return <Typography variant="body-regular">{t('request-owners.empty')}</Typography>
}
